import React from "react";
import { Check, X } from "lucide-react";
import { cvar } from "../../utils";
import { Pill } from "../common/Pill";
import { DocEvidence } from "./DocEvidence";
import type { Approval } from "../../types";

export interface ApprovalsProps {
  approvals: Approval[];
  decide: (key: string, ok: boolean, chosen?: string) => void;
  decideAll: (ok: boolean) => void;
  wsName: (id: string) => string;
  readOnly?: boolean;
}

/* Lawyer-in-the-loop queue. Two modes:
   - readOnly (replay): every item shown with its resolution badge.
   - editable (live run): pending items get Approve / Reject / option buttons. */
export function Approvals({ approvals, decide, decideAll, wsName, readOnly }: ApprovalsProps): JSX.Element {
  const pending = approvals.filter((a) => a.status === "pending");
  const resolved = approvals.filter((a) => a.status !== "pending");
  if (approvals.length === 0)
    return (
      <div className="empty">
        No actions{" "}
        {readOnly ? (
          "were raised in this run."
        ) : (
          <>
            awaiting the lawyer.
            <br />
            Items surface as the spine runs.
          </>
        )}
      </div>
    );
  if (readOnly)
    return (
      <div>
        <div className="up" style={{ marginBottom: 10 }}>
          Lawyer-in-the-loop · {approvals.length} item{approvals.length === 1 ? "" : "s"}
        </div>
        {approvals.map((a) => (
          <div key={a.key} className={"appr" + (a.material ? " mat" : "")}>
            <div className="ahd">
              <Pill
                color={
                  a.type === "conflict"
                    ? "--red"
                    : a.type === "phase"
                      ? "--blue"
                      : a.type === "privilege"
                        ? "--red"
                        : a.type === "participant"
                          ? "--blue"
                          : "--amber"
                }
                label={a.type}
              />
              {a.material && <Pill color="--amber" label="material" />}
              <span className="spacer" />
              <span className="mono tert" style={{ fontSize: 11 }}>
                {wsName(a.wsId)}
              </span>
            </div>
            <div className="al">{a.label}</div>
            <div className="ad" style={{ marginBottom: 6 }}>
              {a.detail}
            </div>
            {a.options && (a.options as any).evidence && (
              <DocEvidence ev={(a.options as any).evidence} />
            )}
            <div className="resb">
              {a.status === "approved" ? (
                <Check size={14} color={cvar("--green")} />
              ) : a.status === "rejected" ? (
                <X size={14} color={cvar("--red")} />
              ) : (
                <span className="tert">●</span>
              )}
              <span
                style={{
                  color: cvar(
                    a.status === "approved" ? "--green" : a.status === "rejected" ? "--red" : "--tert",
                  ),
                }}
              >
                {a.status}
                {a.chosen ? " → " + a.chosen : ""}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <span className="up">Lawyer-in-the-loop · {pending.length} pending</span>
        {pending.length > 0 && (
          <span style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => decideAll(true)}>
              Approve all
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => decideAll(false)}>
              Reject all
            </button>
          </span>
        )}
      </div>
      {pending.map((a) => (
        <div key={a.key} className={"appr" + (a.material ? " mat" : "")}>
          <div className="ahd">
            <Pill
              color={
                a.type === "conflict"
                  ? "--red"
                  : a.type === "phase"
                    ? "--blue"
                    : a.type === "privilege"
                      ? "--red"
                      : "--amber"
              }
              label={a.type}
            />
            {a.material && <Pill color="--amber" label="material" />}
            <span className="spacer" />
            <span className="mono tert" style={{ fontSize: 11 }}>
              {wsName(a.wsId)}
            </span>
          </div>
          <div className="al">{a.label}</div>
          <div className="ad">{a.detail}</div>
          {a.options && (a.options as any).evidence && (
            <DocEvidence ev={(a.options as any).evidence} />
          )}
          <div className="arow">
            {a.options && Array.isArray((a.options as any).options) ? (
              <>
                {(a.options as any).options.map((o: string) => (
                  <button
                    key={o}
                    className="btn btn-primary btn-sm"
                    onClick={() => decide(a.key, true, o)}
                  >
                    <Check size={13} /> Use {o}
                  </button>
                ))}
                <button className="btn btn-ghost btn-sm" onClick={() => decide(a.key, false)}>
                  Reject
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-primary btn-sm" onClick={() => decide(a.key, true)}>
                  <Check size={13} /> Approve
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => decide(a.key, false)}>
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      ))}
      {resolved.length > 0 && (
        <div className="up" style={{ margin: "14px 0 8px" }}>
          Resolved
        </div>
      )}
      {resolved.map((a) => (
        <div key={a.key} className="appr done">
          <div className="resb">
            {a.status === "approved" ? (
              <Check size={14} color={cvar("--green")} />
            ) : (
              <X size={14} color={cvar("--red")} />
            )}
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: cvar(a.status === "approved" ? "--green" : "--red"),
              }}
            >
              {a.status === "approved" ? "Approved" : "Rejected"}
            </span>
            <span>
              {a.label}
              {a.chosen ? " → " + a.chosen : ""}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
